import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const BindingForm = ({ initialValues, onSave, isNew }) => {
  const { t } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const defaultLanguage = customer?.dsokSettings.customer.defaultLanguage;
  const effectiveLanguages = React.useMemo(() => {
    const langs = customer?.dsokSettings?.customer?.languages;
    if (Array.isArray(langs) && langs.length) {
      const defaultLangIndex = langs.indexOf(defaultLanguage);
      if (defaultLangIndex > 0) {
        const [defaultLang] = langs.splice(defaultLangIndex, 1);
        langs.unshift(defaultLang);
      }
    }
    return Array.isArray(langs) && langs.length ? langs : ["sk"];
  }, [customer]);

  const validationSchema = React.useMemo(() => {
    return yup.object({
      bindingName: yup.object().shape(
        effectiveLanguages.reduce((acc, langCode) => {
          acc[langCode] = yup.string().required("Required field");
          return acc;
        }, {})
      ),
    });
  }, [effectiveLanguages]);

  const defaultValues = React.useMemo(
    () => ({
      bindingName: effectiveLanguages.reduce((acc, langCode) => {
        acc[langCode] = "";
        return acc;
      }, {}),
    }),
    [effectiveLanguages]
  );

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(true);
    onSave(values);
  };

  return (
    <Formik
      initialValues={initialValues || defaultValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" mb={3}>
              {isNew
                ? t("pages.dsokBindings.newBinding", {
                    defaultValue: "New Binding",
                  })
                : t("pages.dsokBindings.editBinding", {
                    defaultValue: "Edit Binding",
                  })}
            </Typography>
            <Card sx={{ p: 4 }}>
              <Grid container spacing={3}>
                {effectiveLanguages.map((langCode) => (
                  <Grid item xs={12} md={6} key={langCode}>
                    <JumboTextField
                      fullWidth
                      name={`bindingName.${langCode}`}
                      label={t("pages.dsokBindings.bindingNameWithLang", {
                        defaultValue: "Binding Name - {{lang}}",
                        lang: langCode.toUpperCase(),
                      })}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      {t("buttons.save", { defaultValue: "Save" })}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default BindingForm;
